/* eslint-disable default-case */
import React, {Fragment, useReducer, useEffect, useCallback, useState} from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useParams, useHistory, useLocation } from "react-router-dom";
import useRedirectIfVerified from "../../helpers/use-redirect-if-verified";
import CreatableSelect from "react-select/creatable";
import PhoneInput from "react-phone-number-input";
import { Editor } from "react-draft-wysiwyg";
import { stateToHTML } from "draft-js-export-html";
import { EditorState, ContentState, convertFromHTML } from "draft-js";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import isEqual from 'lodash.isequal';

// leg form import
import DatePicker from "react-date-picker";
import AsyncSelect from "react-select/async";
import TextAreaInput from "../textarea-input";

import timeFormatter from "../../helpers/timeFormatter";
import {
  formatTripDate,
  toDepartureDateMarker,
} from "../../helpers/dates";
import NumberFormat from "react-number-format";

import { toast } from "react-toastify";

import * as Sentry from "@sentry/react";
import JSONPretty from "react-json-pretty";

import ProspectsAutocomplete from "../prospects/prospects-autocomplete";
import CreateProspectModal from "../prospects/create-prospect-modal";
import OperatorAutocomplete from "../opi/operator-autocomplete";

import CreateAuthorizedSignerModal from "../prospects/create-authorized-signer-modal";

import { Datepicker, momentTimezone } from "@mobiscroll/react";
import mobiscroll from "@mobiscroll/react";

import StateDisplay from "../reusable-modules/state-display";

import "@mobiscroll/react/dist/css/mobiscroll.min.css";

import moment from "moment-timezone";

// mui imports
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import Checkbox from "@mui/material/Checkbox";

import ContractCancellationPolicies from "./contract-cancellation-policies";

import ai from "../../helpers/axios";
import TextInput from "../text-input";
import { LoadingButton } from "../shared/loading-button";
import PageHeader from "../shared/page-header";
import UnsavedChangesWarningModal from "../unsaved-changes-warning-modal/unsaved-changes-warning-modal";

momentTimezone.moment = moment;

const toastOptions = {
  position: "top-right",
  autoClose: 5000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
};

const PAGE_TYPE = {
  duplicate: 'duplicate',
  edit: 'edit',
  signedCannotEdit: 'signedCannotEdit',
  newFromLead: 'newFromLead',
  newFromTrip: 'newFromTrip',
};

const copyObjectsArray = array => structuredClone(array);

const reducer = (state, action) => {
  switch (action.type) {
    case "dataBeganDownloading": {
      return {
        ...state,
        loading: true,
      };
    }
    case "fieldChanged": {
      if (action.field === "dusCancellationPolicy") {
        const updatedDus = action.value ? {
          ...action.value,
          value: action.value.value === 0 || !action.value.value ? "" : action.value.value,
          type: action.value.type ? action.value.type : action.value.dus100 ? 1 : "",
        } : action.value;

        return {
          ...state,
          dusCancellationPolicy: updatedDus,
          ...(action.setToInitial && {
            initialFormValues: {
              ...state.initialFormValues,
              dusCancellationPolicy: updatedDus,
            }
          }),
          dusCancellationPolicyChanged: true,
        };
      }

      if (action.field === "contractCancellationPolicies") {
        const updatedContractCancellation = action.value ? action.value.map(({
           amountOfTime,
           unitOfTime,
           value,
           ...rest
         }) => ({
          amountOfTime: amountOfTime || "",
          unitOfTime: unitOfTime || "",
          value: value || "",
          ...rest
        })) : action.value;

        return {
          ...state,
          contractCancellationPolicies: updatedContractCancellation,
          ...(action.setToInitial && {
            initialFormValues: {
              ...state.initialFormValues,
              contractCancellationPolicies: updatedContractCancellation,
            }
          }),
          contractCancellationPoliciesChanged: true,
        };
      }

      return {
        ...state,
        [action.field]: action.value,
        ...(action.setToInitial && {
          initialFormValues: {
            ...state.initialFormValues,
            [action.field]: action.value,
          }
        })
      };
  }

    case "routingProcessed": {
      return {
        ...state,
        legs: action.value,
        routingProcessed: true,
        initialFormValues: {
          ...state.initialFormValues,
          legs: action.value,
        }
      };
    }

    case "prospectAutoSelected": {
      let prospectOptions = [action.value, ...state.prospectOptions];

      return {
        ...state,
        selectedClient: action.value,
        prospectOptions,
      };
    }

    case "legAdded": {
      return {
        ...state,
        legs: [
          ...state.legs,
          {
            departureDate: "",
            departureAirport: {
              airportName: "",
              id: "",
              code: "",
              city: "",
              localCode: "",
            },
            arrivalAirport: {
              airportName: "",
              id: "",
              code: "",
              city: "",
              localCode: "",
            },
            passengers: "",
            aircraft: {
              name: "",
              seats: 8,
              yom: "",
              opi: "",
              id: "",
            },
          },
        ],
        legsDataChanged: true,
      };
    }
    case "legDeleted": {
      let newLegsArray = copyObjectsArray(state.legs);
      newLegsArray.splice(action.index, 1);
      return {
        ...state,
        legs: newLegsArray,
        legsDataChanged: true,
      };
    }
    case "legFieldChanged": {
      let newLegsArray = copyObjectsArray(state.legs);
      newLegsArray[action?.payload?.legIndex][action?.payload?.field] =
        action?.payload?.value;
      return {
        ...state,
        legs: newLegsArray,
        legsDataChanged: true,
      };
    }

    case "flightTimesDownloaded": {
      let { index, values } = action;

      let newLegsArray = copyObjectsArray(state.legs);

      values.forEach((item) => {
        if (item.status === "rejected" && isNaN(item?.value?.legIndex)) {
          toast.error(
            `Flight times not available for Leg ${
              item?.value?.legIndex + 1
            }, please input manually.`,
            toastOptions
          );
          return;
        } else if (item.status === "rejected") {
          toast.error(
            `Flight times not available - please input manually.`,
            toastOptions
          );
          return;
        }

        if (
          !item?.value?.timeWithWindAdjustment ||
          !item?.value?.status ||
          isNaN(item?.value?.legIndex)
        ) {
          toast.error(
            "Flight times not available - please input manually.",
            toastOptions
          );
          return;
        }

        newLegsArray[item.value.legIndex].ete = timeFormatter(
          item.value.timeWithWindAdjustment
        );
      });

      return {
        ...state,
        legs: newLegsArray,
        initialFormValues: {
          ...state.initialFormValues,
          legs: newLegsArray,
        }
      };
    }

    case "unsavedChanges": {
      const updatedUnsavedChanges = {...state.unsavedChanges};

      if (!action.value) {
        delete updatedUnsavedChanges[action.field];
      } else {
        updatedUnsavedChanges[action.field] = true;
      }

      return {
        ...state,
        unsavedChanges: updatedUnsavedChanges
      };
    }

    default:
      break;
  }
  return state;
};

let contractNotesHTML = ` <h3>Message</h3>
<p id="contract-message-body">
  ETAs, ETDs, and ETEs are not guaranteed, are estimates, and may vary due to airport delays, weather, and/or other events outside evoJets’ control. Technical stops for fuel and customs clearance are not listed above, may apply, and are outside of evoJets' control.
</p>

<p class="italics">
  Cancellation fees include any costs incurred by evoJets in association with this trip.
</p>

<p>
  Expenses for catering, ground transportation, itinerary changes, and any other incidentals incurred after booking will be charged to the credit card held on file, and may not be processed for up to 60 days after the completion of this trip.
</p>`;

const contractNotesConverted = convertFromHTML(contractNotesHTML);

const defaultContractNotesEditorState = EditorState.createWithContent(
  ContentState.createFromBlockArray(
    contractNotesConverted.contentBlocks,
    contractNotesConverted.entityMap
  )
);

const NewContractContent = (props) => {
  const history = useHistory();
  const location = useLocation();

  let { tripId, contractId, quoteId, aircraftOptionId, leadId, action } =
    useParams();

  let initialState = {
    contractIsNotSigned: false,
    modalPhoneNumber: "",
    selectedClient: null,
    prospect: null,
    createProspectModalIsOpen: false,
    createAuthorizedSignerModalIsOpen: false,
    routingProcessed: false,

    prospectOptions: [],
    loading: true,
    useDifferentAuthorizedSigner: false,
    companyName: "",
    signerName: "",
    signerEmail: "",
    signerPhone: "",
    signerPhoneCountry: "US",

    //todo - update sample legs data

    authorizedSigners: [],

    paymentType: "wire",
    flightCharges: "",
    FET: "",
    ccFee: "",
    segmentFees: "",
    catering: "",
    groundTransportation: "",
    otherExpense: "",
    totalCharges: "",

    email: "",
    emailInputValue: "",
    subject: "",

    cc: [],
    ccInputValue: "",

    contractNotesEditor: EditorState.createEmpty(),

    supplementalTermsEditor: EditorState.createEmpty(),

    contractCancellationPolicies: [
      {
        type: 1,
        value: "",
        amountOfTime: "",
        unitOfTime: "",
      },
    ],

    dusCancellationPolicy: {
      type: 1,
      value: "",
      dus100: false,
    },

    legs: [],

    isFormLoading: false,
    tripTag: "",
    contractTag: "",

    legsDataChanged: false,
    contractCancellationPoliciesChanged: false,
    dusCancellationPolicyChanged: false,
    initialFormValues: {
      supplementalTermsEditor: EditorState.createEmpty(),
      contractNotesEditor: EditorState.createEmpty(),
      paymentType: "wire",
      flightCharges: "",
      ccFee: "",
      FET: "",
      segmentFees: "",

      contractCancellationPolicies: [
        {
          type: 1,
          value: "",
          amountOfTime: "",
          unitOfTime: "",
        },
      ],
      dusCancellationPolicy: {
        type: 1,
        value: "",
        dus100: false,
      },
      legs: [],
    },
    unsavedChanges: {},
  };

  const leadDataFromLocation = location?.state?.lead;
  const sourcingRecords = location?.state?.sourcingRecords;

  const [state, dispatch] = useReducer(reducer, initialState);

  const setUnsavedChanges = useCallback(({field, value}) => {
    dispatch({
      type: "unsavedChanges",
      field,
      value
    })
  }, []);

  const { getAccessTokenSilently, loginWithRedirect, user } = useAuth0();

  const [tripVerified, setTripVerified] = useState(false);
  const [tripLocked, setTripLocked] = useState(false);
  const superAdmin = (user?.["https://new.evojets.com/roles"] || []).some(
    (role) => role === "Super Admin",
  );
  const verificationAdmin = !!user?.["https://new.evojets.com/appMetadata"]?.isVerificationAdmin;
  useRedirectIfVerified(tripVerified, superAdmin, tripId, tripLocked, verificationAdmin);

  const processSourcing = (sourcingRecords) => {
    sourcingRecords.forEach((item) => {
      if (item.operatorPrice) {
        dispatch({
          type: "fieldChanged",
          field: "flightCharges",
          value: item.operatorPrice,
          setToInitial: true,
        });
      }
      if (item?.fet?.amount && !isNaN(item?.fet?.amount)) {
        dispatch({
          type: "fieldChanged",
          field: "FET",
          value: item.fet.amount.toFixed(2),
          setToInitial: true,
        });
      }

      if (item?.segmentFees?.amount && !isNaN(item?.segmentFees?.amount)) {
        dispatch({
          type: "fieldChanged",
          field: "segmentFees",
          value: item.segmentFees.amount.toFixed(2),
          setToInitial: true,
        });
      }

      if (item?.ccFee) {
        dispatch({
          type: "fieldChanged",
          field: "ccFee",
          value: item.ccFee,
          setToInitial: true,
        });
      }

      if (item?.paymentType) {
        dispatch({
          type: "fieldChanged",
          field: "paymentType",
          value: item.paymentType,
          setToInitial: true,
        });
      }
    });
  };

  const prepareNewContract = async () => {
    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    //handle creation from trip
    if (tripId) {
      await getTripData(tripId);
    }

    dispatch({
      type: "fieldChanged",
      field: "contractIsNotSigned",
      value: true,
    });

    if (leadDataFromLocation?._id) {
      let response = await ai
        .auth(token)
        .get(`/api/leads/${leadDataFromLocation?._id}/withQuoteDataAndTZ`)
        .catch((error) => {
          console.error(error);
          Sentry.captureException(error);
        });

      if (response && response.data) {
        dispatch({
          type: "fieldChanged",
          field: "leadData",
          value: response.data,
          setToInitial: true,
        });

        dispatch({ type: "fieldChanged", field: "loading", value: false });

        parseClientData(response.data?.clientData);

        processRouting(response.data, sourcingRecords);

        if (response.data.tripIds?.length) {
          //todo
          // console.log("LEAD HAS TRIP");
        }
      }
    }

    if (leadId && quoteId && aircraftOptionId) {
      const response = await ai
        .auth(token)
        .get(`/api/leads/${leadId}/withQuoteDataAndTZ`)
        .catch((error) => {
          console.error(error);
          Sentry.captureException(error);
        });

      if (response && response.data) {
        dispatch({
          type: "fieldChanged",
          field: "leadData",
          value: response.data,
        });

        dispatch({ type: "fieldChanged", field: "loading", value: false });

        parseClientData(response.data?.clientData);

        processRouting(response.data, sourcingRecords);

        const quote = response.data.quoteData?.find(
          ({ _id }) => _id === quoteId
        );
        if (quote) {
          const aircraftOption = quote?.aircraftOptions.find(
            ({ _id }) => _id === aircraftOptionId
          );

          processQuoteData(quote.legs, aircraftOption);
        }
      }
    }
  };

  const processSubject = () => {
    let estimate = state?.leadData?.estimateData;

    if (!estimate?.length) {
      return;
    }

    estimate = estimate[0];

    let departDate = formatTripDate(estimate.departureDateString, "DD MMM");
    let returnDate = null;

    let subjectText = `${state?.leadData?.firstName} ${state?.leadData?.lastName} | ${departDate} | ${estimate.departureAirportCode}-${estimate.arrivalAirportCode}`;

    if (state?.leadData?.tripType === 2) {
      returnDate = formatTripDate(estimate.returnDateString, "DD MMM");

      subjectText = `${state?.leadData?.firstName} ${state?.leadData?.lastName} | ${departDate} - ${returnDate} | ${estimate.departureAirportCode}-${estimate.arrivalAirportCode}`;
    }

    dispatch({
      type: "fieldChanged",
      field: "subject",
      value: subjectText,
    });
  };

  const processRouting = async (leadData, sourcingRecords) => {
    let estimate = null;
    if (leadData?.estimateData?.length) {
      estimate = { ...leadData?.estimateData[0] };
    }

    if (!estimate) {
      return;
    }

    let departureAirportTZ = null;
    let arrivalAirportTZ = null;
    if (estimate.departureAirportTZ?.length) {
      departureAirportTZ = estimate.departureAirportTZ[0];
    }

    if (estimate.arrivalAirportTZ?.length) {
      arrivalAirportTZ = estimate.arrivalAirportTZ[0];
    }

    let formattedRoutingLegOne = {
      departureDate: toDepartureDateMarker(estimate.departureDateString),
      departureAirport: {
        airportName: estimate.departureAirportName,
        id: estimate.departureAirportId,
        code: estimate.departureAirportCode,
        city: estimate.departureAirportCity,
        localCode: estimate.departureAirportLocalCode,
      },
      arrivalAirport: {
        airportName: estimate.arrivalAirportName,
        id: estimate.arrivalAirportId,
        code: estimate.arrivalAirportCode,
        city: estimate.arrivalAirportCity,
        localCode: estimate.arrivalAirportLocalCode,
      },
      passengers: estimate.passengerCount,
      etd: "",
      eta: "",
      ete: "",
      departureTimeZone: departureAirportTZ,
      arrivalTimeZone: arrivalAirportTZ,
      aircraft: {},
    };

    let formattedRoutingLegTwo = null;

    if (leadData?.tripType === 2) {
      formattedRoutingLegTwo = {
        departureDate: toDepartureDateMarker(estimate.returnDateString),
        arrivalAirport: {
          airportName: estimate.departureAirportName,
          id: estimate.departureAirportId,
          code: estimate.departureAirportCode,
          city: estimate.departureAirportCity,
          localCode: estimate.departureAirportLocalCode,
        },
        departureAirport: {
          airportName: estimate.arrivalAirportName,
          id: estimate.arrivalAirportId,
          code: estimate.arrivalAirportCode,
          city: estimate.arrivalAirportCity,
          localCode: estimate.arrivalAirportLocalCode,
        },
        passengers: estimate.passengerCount,
        etd: "",
        eta: "",
        ete: "",
        departureTimeZone: arrivalAirportTZ,
        arrivalTimeZone: departureAirportTZ,
      };
    }

    if (sourcingRecords) {
      //handle roundtrip
      //todo

      let legOne = sourcingRecords[0];
      formattedRoutingLegOne.aircraft = {
        name: legOne?.aircraft?.name,
        seats: legOne?.aircraft?.seats,
        yom: legOne.yom,
        id: legOne?.aircraft?.id,
      };

      formattedRoutingLegOne.selectedOperator = {
        name: legOne?.operator?.name,
        id: legOne?.operator?.id,
        value: legOne?.operator?.id,
        label: legOne?.operator?.name,
      };

      if (formattedRoutingLegTwo) {
        formattedRoutingLegTwo.aircraft = {
          name: legOne?.aircraft?.name,
          seats: legOne?.aircraft?.seats,
          yom: legOne.yom,
          id: legOne?.aircraft?.id,
        };

        formattedRoutingLegTwo.selectedOperator = {
          name: legOne?.operator?.name,
          id: legOne?.operator?.id,
          value: legOne?.operator?.id,
          label: legOne?.operator?.name,
        };
      }
    }

    let formattedRouting = [formattedRoutingLegOne];

    if (formattedRoutingLegTwo) {
      formattedRouting.push(formattedRoutingLegTwo);
    }

    dispatch({
      type: "routingProcessed",
      value: formattedRouting,
    });
  };

  const processQuoteData = async (legs, aircraftOption) => {
    const formattedLegs = legs.map((leg) => ({
      departureDate: leg.departureDate,
      departureAirport: leg.departureAirport,
      arrivalAirport: leg.arrivalAirport,
      passengers: leg.passengers,
      aircraft: {
        id: aircraftOption.aircraft.id,
        name: aircraftOption.aircraft.name,
        seats: aircraftOption.aircraft.seats,
        yom: aircraftOption.aircraft.yom,
      },
      etd: "",
      eta: "",
      ete: "",
      departureTimeZone: leg.departureAirportTZ?.[0] || null,
      arrivalTimeZone: leg.arrivalAirportTZ?.[0] || null,
    }));

    dispatch({
      type: "routingProcessed",
      value: formattedLegs,
    });

    dispatch({
      type: "fieldChanged",
      field: "flightCharges",
      value: aircraftOption.totalCost,
      setToInitial: true,
    });

    for (const [legIndex, legData] of formattedLegs.entries()) {
      await handleFlightTimeCalculationByIndex(legData, legIndex, true);
    }
  };

  useEffect(() => {
    handleFlightTimeCalculation();
  }, [state.routingProcessed]);

  const parseAuthorizedSigners = (clientData) => {
    if (!clientData?.additionalUsers?.length) {
      return false;
    }

    let authorizedSigners = clientData.additionalUsers.flatMap((item) => {
      if (!item?.isAuthorizedSigner) {
        return [];
      }
      let label = `${item.firstName} ${item.lastName} - ${item.relationship}`;
      let value = item?._id;
      return {
        ...item,
        label,
        value,
      };
    });

    dispatch({
      type: "fieldChanged",
      field: "authorizedSigners",
      value: authorizedSigners,
      setToInitial: true,
    });
  };

  const parseClientData = (clientData) => {
    if (!clientData) {
      //null
      return false;
    }

    if (!clientData?._id || !clientData?.firstName || !clientData?.lastName) {
      //no contact info
      return false;
    }

    if (clientData?.isBusiness && !clientData?.companyName) {
      //client is business but missing company name
      return false;
    }

    let label = `${clientData.firstName} ${clientData?.lastName}`;
    let value = clientData?._id;

    if (clientData?.isBusiness) {
      label = clientData.companyName;

      dispatch({
        type: "fieldChanged",
        field: "companyName",
        value: clientData.companyName,
        setToInitial: true,
      });
    }

    if (clientData?.firstName && clientData?.lastName) {
      dispatch({
        type: "fieldChanged",
        field: "signerName",
        value: `${clientData.firstName} ${clientData?.lastName}`,
        setToInitial: true,
      });
    }

    if (clientData?.phoneNumbers?.length) {
      if (
        clientData?.phoneNumbers[0]?.number &&
        clientData?.phoneNumbers[0]?.countryCode
      ) {
        dispatch({
          type: "fieldChanged",
          field: "signerPhone",
          value: clientData?.phoneNumbers[0]?.number,
          setToInitial: true,
        });
        dispatch({
          type: "fieldChanged",
          field: "signerPhoneCountry",
          value: clientData?.phoneNumbers[0]?.countryCode,
          setToInitial: true,
        });
      }
    } else if (clientData?.workPhones?.length) {
      dispatch({
        type: "fieldChanged",
        field: "signerPhone",
        value: clientData?.workPhones[0],
        setToInitial: true,
      });
    } else if (clientData?.mobilePhones?.length) {
      dispatch({
        type: "fieldChanged",
        field: "signerPhone",
        value: clientData?.mobilePhones[0],
        setToInitial: true,
      });
    }

    parseAuthorizedSigners(clientData);

    if (clientData?.emails?.length) {
      dispatch({
        type: "fieldChanged",
        field: "email",
        value: clientData.emails[0],
        setToInitial: true,
      });

      dispatch({
        type: "fieldChanged",
        field: "signerEmail",
        value: clientData.emails[0],
        setToInitial: true,
      });
    }

    dispatch({
      type: "fieldChanged",
      field: "selectedClient",
      value: {
        label,
        value,
        ...clientData,
      },
      setToInitial: true,
    });
  };

  const processContractAndTripData = async (contractAndTripData) => {
    console.log("PROCESS CONTRACT AND TRIP DATA");

    setTripVerified(!!contractAndTripData?.tripData?.verified);
    setTripLocked(!!contractAndTripData?.tripData?.lockState?.locked);

    let legs = contractAndTripData.legs.map((leg) => {
      let selectedOperator = null;
      if (leg?.operator?.id) {
        selectedOperator = {
          _id: leg.operator.id,
          name: leg.operator.name,
          value: leg.operator.id,
          label: leg.operator.name,
        };
      }

      let departureTimeZone = null;
      let arrivalTimeZone = null;
      let departureDate = moment(leg.departureDate);

      departureDate.isValid()
        ? (departureDate = departureDate.toDate())
        : (departureDate = "");

      return {
        departureDate,
        departureAirport: leg.departureAirport,
        arrivalAirport: leg.arrivalAirport,
        passengers: leg.passengers,
        etd: leg.etd ?? "",
        eta: leg.eta ?? "",
        ete: leg.ete ?? "",
        selectedOperator,
        aircraft: leg.aircraft,
        departureTimeZone: leg?.departureTimeZone,
        arrivalTimeZone: leg?.arrivalTimeZone,
      };
    });

    // Handle contract notes (for historical contracts)

    if (contractAndTripData?.contractNotes) {
      let contractNotes = convertFromHTML(contractAndTripData.contractNotes);

      let contractNotesEditorState = EditorState.createWithContent(
        ContentState.createFromBlockArray(
          contractNotes.contentBlocks,
          contractNotes.entityMap
        )
      );

      if (
        contractAndTripData.contractNotes === "<p></p>" ||
        contractAndTripData.contractNotes.trim() === ""
      ) {
        contractNotesEditorState = null;
      }

      dispatch({
        type: "fieldChanged",
        field: "contractNotesEditor",
        value: contractNotesEditorState,
        setToInitial: true,
      });
    }

    if (contractAndTripData?.supplementalTerms) {
      let supplementalTerms = convertFromHTML(
        contractAndTripData.supplementalTerms
      );

      let supplementalTermsEditorState = EditorState.createWithContent(
        ContentState.createFromBlockArray(
          supplementalTerms.contentBlocks,
          supplementalTerms.entityMap
        )
      );

      dispatch({
        type: "fieldChanged",
        field: "supplementalTermsEditor",
        value: supplementalTermsEditorState,
        setToInitial: true,
      });
    }

    //pricing
    Object.keys(contractAndTripData.pricing).forEach((item) => {
      let itemValue = contractAndTripData.pricing[item];

      if (typeof itemValue === "number" && itemValue !== 0) {
        itemValue = itemValue.toFixed(2);
      }

      if (itemValue === 0) {
        itemValue = "";
      }

      dispatch({
        type: "fieldChanged",
        field: item,
        value: itemValue,
        setToInitial: true,
      });
    });

    //recipient fields
    Object.keys(contractAndTripData.recipientFields).forEach((item) => {
      let itemValue = contractAndTripData.recipientFields[item];

      if (item === "cc" && itemValue?.length) {
        itemValue = itemValue.map((item) => {
          return { label: item, value: item };
        });
      }

      dispatch({
        type: "fieldChanged",
        field: item,
        value: itemValue,
        setToInitial: true,
      });
    });

    if (contractAndTripData.clientData) {
      let i = contractAndTripData.clientData;
      let label = `${contractAndTripData.clientData?.firstAndLastName} - ${contractAndTripData.clientData?.emails[0]}`;

      if (!i?.firstAndLastName) {
        if (!i.firstName) {
          i.firstName = "";
        }

        if (!i.lastName) {
          i.lastName = "";
        }

        label = `${i.firstName} ${i.lastName} - ${i.emails[0]}`;
      }

      if (i.companyName) {
        label = `${label} - ${i.companyName}`;
      }

      if (i.assigneeName && i.assigneeId) {
        label = `${label} - ${i.assigneeName}`;
      }

      contractAndTripData.clientData.label = label;
    }

    if (contractAndTripData?.dusCancellationPolicy) {
      dispatch({
        type: "fieldChanged",
        field: "dusCancellationPolicy",
        value: contractAndTripData?.dusCancellationPolicy,
        setToInitial: true,
      });
    }

    if (contractAndTripData?.contractCancellationPolicies) {
      dispatch({
        type: "fieldChanged",
        field: "contractCancellationPolicies",
        value: contractAndTripData?.contractCancellationPolicies,
        setToInitial: true,
      });
    }

    dispatch({
      type: "fieldChanged",
      field: "contractIsNotSigned",
      value: contractAndTripData.stage !== "signed",
    });

    dispatch({
      type: "fieldChanged",
      field: "companyName",
      value: contractAndTripData.companyName || "",
      setToInitial: true,
    });

    dispatch({
      type: "fieldChanged",
      field: "signerName",
      value: contractAndTripData.signerName || "",
      setToInitial: true,
    });

    dispatch({
      type: "fieldChanged",
      field: "signerPhone",
      value: contractAndTripData.signerPhone || "",
      setToInitial: true,
    });

    dispatch({
      type: "fieldChanged",
      field: "signerPhoneCountry",
      value: contractAndTripData.signerPhoneCountry || "US",
      setToInitial: true,
    });

    dispatch({
      type: "fieldChanged",
      field: "signerEmail",
      value: contractAndTripData.signerEmail || "",
      setToInitial: true,
    });

    dispatch({
      type: "fieldChanged",
      field: "selectedClient",
      value: contractAndTripData.clientData,
      setToInitial: true,
    });

    dispatch({
      type: "fieldChanged",
      field: "prospect",
      value: contractAndTripData.clientData,
      setToInitial: true,
    });

    dispatch({
      type: "fieldChanged",
      field: "useDifferentAuthorizedSigner",
      value: contractAndTripData.useDifferentAuthorizedSigner,
      setToInitial: true,
    });

    dispatch({
      type: "fieldChanged",
      field: "leadData",
      value: contractAndTripData.leadData,
      setToInitial: true,
    });

    dispatch({
      type: "fieldChanged",
      field: "tripTag",
      value: contractAndTripData?.tripData?.tripTag,
    });

    dispatch({
      type: "fieldChanged",
      field: "contractTag",
      value: contractAndTripData?.contractTag,
    });

    dispatch({
      type: "fieldChanged",
      field: "legs",
      value: legs,
      setToInitial: true,
    });

    dispatch({ type: "fieldChanged", field: "loading", value: false });
  };

  const processTripData = async (tripData) => {
    setTripVerified(!!tripData?.verified);
    setTripLocked(!!tripData?.lockState?.locked);
    if (!tripData || !tripData?.contractData?.length) {
      return false;
    }

    dispatch({
      type: "fieldChanged",
      field: "tripTag",
      value: tripData.tripTag,
    });

    let contractAndTripData = tripData.contractData.sort(
      (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)
    );
    contractAndTripData = contractAndTripData[0];

    let legs = contractAndTripData.legs.map((leg) => {
      let selectedOperator = null;
      if (leg?.operator?.id) {
        selectedOperator = {
          _id: leg.operator.id,
          name: leg.operator.name,
          value: leg.operator.id,
          label: leg.operator.name,
        };
      }

      let departureTimeZone = null;
      let arrivalTimeZone = null;
      let departureDate = moment(leg.departureDate);

      departureDate.isValid()
        ? (departureDate = departureDate.toDate())
        : (departureDate = "");

      if (contractAndTripData?.departureAirports?.length) {
        let matchingDepartureAirport =
          contractAndTripData?.departureAirports.find(
            (item) => item._id === leg.departureAirport?.id
          );
        //get lat and lon

        if (matchingDepartureAirport) {
          if (departureTimeZone?.length) {
            departureTimeZone = departureTimeZone[0];
          }
        }

        // pass to datepicker component for etd
      }

      if (contractAndTripData?.departureAirports?.length) {
        let matchingArrivalAirport = contractAndTripData?.arrivalAirports.find(
          (item) => item._id === leg.arrivalAirport?.id
        );
      }

      if (contractAndTripData?.supplementalTerms) {
        let supplementalTerms = convertFromHTML(
          contractAndTripData.supplementalTerms
        );

        let supplementalTermsEditorState = EditorState.createWithContent(
          ContentState.createFromBlockArray(
            supplementalTerms.contentBlocks,
            supplementalTerms.entityMap
          )
        );

        dispatch({
          type: "fieldChanged",
          field: "supplementalTermsEditor",
          value: supplementalTermsEditorState,
          setToInitial: true,
        });
      }

      return {
        departureDate,
        departureAirport: leg.departureAirport,
        arrivalAirport: leg.arrivalAirport,
        passengers: leg.passengers,
        etd: leg.etd ?? "",
        eta: leg.eta ?? "",
        ete: leg.ete ?? "",
        selectedOperator,
        aircraft: leg.aircraft,
        departureTimeZone,
        arrivalTimeZone,
      };
    });

    if (contractAndTripData?.contractNotes) {
      let contractNotes = convertFromHTML(contractAndTripData.contractNotes);

      let contractNotesEditorState = EditorState.createWithContent(
        ContentState.createFromBlockArray(
          contractNotes.contentBlocks,
          contractNotes.entityMap
        )
      );

      dispatch({
        type: "fieldChanged",
        field: "contractNotesEditor",
        value: contractNotesEditorState,
        setToInitial: true,
      });
    }

    //pricing
    Object.keys(contractAndTripData.pricing).forEach((item) => {
      let itemValue = contractAndTripData.pricing[item];

      if (typeof itemValue === "number" && itemValue !== 0) {
        itemValue = itemValue.toFixed(2);
      }

      if (itemValue === 0) {
        itemValue = "";
      }

      dispatch({
        type: "fieldChanged",
        field: item,
        value: itemValue,
        setToInitial: true,
      });
    });

    //recipient fields
    Object.keys(contractAndTripData.recipientFields).forEach((item) => {
      let itemValue = contractAndTripData.recipientFields[item];

      if (item === "cc" && itemValue?.length) {
        itemValue = itemValue.map((item) => {
          return { label: item, value: item };
        });
      }

      dispatch({
        type: "fieldChanged",
        field: item,
        value: itemValue,
        setToInitial: true,
      });
    });

    if (contractAndTripData.clientData) {
      let i = contractAndTripData.clientData;
      let label = `${contractAndTripData.clientData?.firstAndLastName} - ${contractAndTripData.clientData?.emails[0]}`;

      if (!i?.firstAndLastName) {
        if (!i.firstName) {
          i.firstName = "";
        }

        if (!i.lastName) {
          i.lastName = "";
        }

        label = `${i.firstName} ${i.lastName} - ${i.emails[0]}`;
      }

      if (i.companyName) {
        label = `${label} - ${i.companyName}`;
      }

      if (i.assigneeName && i.assigneeId) {
        label = `${label} - ${i.assigneeName}`;
      }

      contractAndTripData.clientData.label = label;
    }

    if (contractAndTripData?.data?.dusCancellationPolicy) {
      dispatch({
        type: "fieldChanged",
        field: "dusCancellationPolicy",
        value: contractAndTripData?.data?.dusCancellationPolicy,
        setToInitial: true,
      });
    }

    if (contractAndTripData?.data?.contractCancellationPolicies) {
      dispatch({
        type: "fieldChanged",
        field: "contractCancellationPolicies",
        value: contractAndTripData?.data?.contractCancellationPolicies,
        setToInitial: true,
      });
    }

    dispatch({
      type: "fieldChanged",
      field: "selectedClient",
      value: contractAndTripData.clientData,
      setToInitial: true,
    });

    dispatch({
      type: "fieldChanged",
      field: "useDifferentAuthorizedSigner",
      value: contractAndTripData.useDifferentAuthorizedSigner,
      setToInitial: true,
    });

    dispatch({
      type: "fieldChanged",
      field: "companyName",
      value: contractAndTripData?.companyName || "",
      setToInitial: true,
    });

    dispatch({
      type: "fieldChanged",
      field: "signerName",
      value: contractAndTripData.signerName || "",
      setToInitial: true,
    });

    dispatch({
      type: "fieldChanged",
      field: "signerPhone",
      value: contractAndTripData.signerPhone || "",
      setToInitial: true,
    });

    dispatch({
      type: "fieldChanged",
      field: "signerPhoneCountry",
      value: contractAndTripData.phoneCountry || "US",
      setToInitial: true,
    });

    dispatch({
      type: "fieldChanged",
      field: "signerEmail",
      value: contractAndTripData.signerEmail || "",
      setToInitial: true,
    });

    dispatch({
      type: "fieldChanged",
      field: "leadData",
      value: contractAndTripData.leadData,
      setToInitial: true,
    });

    dispatch({
      type: "fieldChanged",
      field: "legs",
      value: legs,
      setToInitial: true,
    });
    dispatch({ type: "fieldChanged", field: "loading", value: false });
  };

  const getTripData = async (tripId) => {
    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    console.log("GET TRIP DATA");

    let { data: tripData } = await ai.auth(token).get(`/api/trips/${tripId}`);

    processTripData(tripData);
  };

  const getContractAndTripData = async () => {
    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    console.log("GET CONTRACT AND TRIP DATA");

    let { data: contractAndTripData } = await ai
      .auth(token)
      .get(`/api/trips/${tripId}/contracts/${contractId}`);

    processContractAndTripData(contractAndTripData);
  };

  useEffect(() => {
    if (!contractId || contractId === "new") {
      prepareNewContract();
    } else {
      getContractAndTripData();
    }

    document.title = "Contract Builder | evoJets HQ";
  }, []);

  const handleTextInputChange = (e) => {
    setUnsavedChanges({
      field: e.target.name,
      value: e.target.value !== state.initialFormValues[e.target.name],
    })

    return dispatch({
      type: "fieldChanged",
      field: e.target.name,
      value: e.target.value,
    });
  };

  const createOption = (label) => ({
    label,
    value: label,
  });

  const handleAddLeg = (event) => {
    event.preventDefault();

    dispatch({ type: "legAdded" });
  };

  const loadAirportOptions = async (inputValue) => {
    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });
    let { data } = await ai
      .auth(token)
      .get(`/api/airports/search?q=${inputValue}&includeTZ=true`);

    let options = [];
    if (data.length >= 1) {
      options = data.map((i) => ({
        value: i._id,
        label: i.airportName,
        code: i.code,
        city: i.city,
        localCode: i.localCode,
        lat: i.lat,
        lon: i.lon,
        tz: i.tz,
      }));
    }

    return options;
  };
  const loadAircraftOptions = async (inputValue) => {
    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });
    let { data } = await ai
      .auth(token)
      .get(`/api/aircrafts/search?q=${inputValue}`, {
        params: {
          query: inputValue,
          page: 1,
          countPerPage: 50,
        },
      });

    if (data.aircraft.length >= 1) {
      return data.aircraft.map((aircraft) => {
        return {
          value: aircraft._id,
          label: aircraft.name,
          id: aircraft._id,
          yom: aircraft.yom,
          name: aircraft.name,
          opi: aircraft.opi,
          seats: aircraft.seats,
          speed: aircraft.cruiseSpeed,
        };
      });
    } else {
      return [];
    }
  };

  const handleClientChange = (selectedClient) => {
    setUnsavedChanges({
      field: 'selectedClient',
      value: selectedClient?._id !== state.initialFormValues.selectedClient?._id,
    });

    dispatch({
      type: "fieldChanged",
      field: "selectedClient",
      value: selectedClient ? {
        _id: selectedClient.value,
        ...selectedClient,
      } : selectedClient,
    });
  };

  const handleClientOptionCreation = (inputValue) => {
    console.log("HANDLE CLIENT OPTION CREATION", inputValue);

    dispatch({
      type: "fieldChanged",
      field: "createProspectModalIsOpen",
      value: true,
    });
  };

  const handleAuthorizedSignerOptionCreation = (inputValue) => {
    console.log("HANDLE AUTHORIZED SIGNER CREATION", inputValue);

    dispatch({
      type: "fieldChanged",
      field: "createAuthorizedSignerModalIsOpen",
      value: true,
    });
  };

  const handleContractSubmit = async (e) => {
    e.preventDefault();

    if (state.isFormLoading) {
      return;
    }

    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    let legs = state.legs.map(
      ({
        departureDate,
        departureAirport,
        arrivalAirport,
        passengers,
        etd,
        eta,
        ete,
        aircraft,
        arrivalTimeZone,
        departureTimeZone,
      }) => {
        return {
          departureDate,
          departureAirport,
          arrivalAirport,
          passengers,
          etd,
          eta,
          ete,
          aircraft,
          arrivalTimeZone,
          departureTimeZone,
        };
      }
    );

    let {
      flightCharges,
      FET,
      paymentType,
      segmentFees,
      catering,
      groundTransportation,
      otherExpense,
      subject,
      email,
      cc,
      ccFee,
      signerName,
      signerEmail,
      signerPhone,
      signerPhoneCountry,
    } = state;

    if (cc.length) {
      cc = cc.map((item) => item.value);
    }
    let sourcingRecordIds = [];
    if (sourcingRecords?.length) {
      sourcingRecordIds = sourcingRecords.map((i) => i._id);
    }

    let contractNotes = stateToHTML(
      state.contractNotesEditor.getCurrentContent()
    );

    // Only include contractNotes if it has actual content
    if (contractNotes === "<p></p>" || contractNotes.trim() === "") {
      contractNotes = null;
    }

    let supplementalTerms = stateToHTML(
      state.supplementalTermsEditor.getCurrentContent()
    );

    // Only include supplementalTerms if it has actual content
    if (supplementalTerms === "<p></p>" || supplementalTerms.trim() === "") {
      supplementalTerms = null;
    }

    let contractData = {
      clientId: state.selectedClient?._id,
      useDifferentAuthorizedSigner: state.useDifferentAuthorizedSigner,
      authorizedSignerId: state.selectedAuthorizedSigner?._id,
      legs,
      pricing: {
        flightCharges: +flightCharges,
        FET: +FET,
        paymentType,
        segmentFees: +segmentFees,
        catering: +catering,
        groundTransportation: +groundTransportation,
        otherExpense: +otherExpense,
        ccFee: +ccFee,
      },
      recipientFields: {
        subject,
        email,
        cc,
      },
      leadId: state?.leadData?._id,
      sourcingRecordIds,
      contractNotes,
      supplementalTerms,
      contractCancellationPolicies: state.contractCancellationPolicies,
      dusCancellationPolicy: state.dusCancellationPolicy,
      companyName: state?.companyName,
      signerName,
      signerEmail,
      signerPhone,
      signerPhoneCountry,
    };

    if (tripId) {
      contractData.tripId = tripId;
    }

    if (action && action === "new-lead") {
      contractData.createLead = true;
    }

    let response = false;

    dispatch({
      type: "fieldChanged",
      field: "isFormLoading",
      value: true,
    });

    try {
      if (!contractId || contractId === "new") {
        if (tripId) {
          //create new for trip
          response = await ai
            .auth(token)
            .post(`/api/trips/${tripId}/contracts/create`, contractData)
            .catch((error) => {
              console.error(error);
              Sentry.captureException(error);
            });
        } else {
          //create new and create trip
          response = await ai
            .auth(token)
            .post(`/api/trips/contracts/create`, contractData)
            .catch((error) => {
              console.error(error);
              Sentry.captureException(error);
            });
        }
      }

      if (contractId && contractId !== "new") {
        if (action === "duplicate") {
          response = await ai
            .auth(token)
            .post(`/api/trips/${tripId}/contracts/duplicate`, contractData)
            .catch((error) => {
              console.error(error);
              Sentry.captureException(error);
            });
        } else {
          //edit
          response = await ai
            .auth(token)
            .put(`/api/trips/${tripId}/contracts/${contractId}`, contractData)
            .catch((error) => {
              console.error(error);
              Sentry.captureException(error);
            });
        }
      }
    } finally {
      dispatch({
        type: "fieldChanged",
        field: "isFormLoading",
        value: false,
      });
    }

    if (response && response.data) {
      dispatch({
        type: "fieldChanged",
        field: "tripData",
        value: response.data,
      });

      dispatch({
        type: "fieldChanged",
        field: "unsavedChanges",
        value: {},
      });

      if (action === "duplicate") {
        setTimeout(() => {
          toast.success("Contract duplicated successfully!", toastOptions);
        }, 500);

        history.push(`/trips/${response.data?.tripId}`);
      } else {
        setTimeout(() => {
          toast.success("Contract saved successfully!", toastOptions);
        }, 500);

        if (location.pathname === "/contracts/new") {
          history.replace(`/trips/${response.data?.tripId}`);
        }
        history.push(
          `/trips/${response.data?.tripId}/contracts/${response.data._id}/preview`
        );
      }
    }
  };

  const handleAirportChange = async (legIndex, airport, airportIndex) => {
    let legData = { ...state.legs[legIndex] };

    if (airportIndex === 0) {
      legData.departureAirport = {
        id: airport.value,
        name: airport.label,
      };

      let departureTimeZone = null;
      if (airport.tz && airport.tz.length) {
        departureTimeZone = airport.tz[0];
      }

      if (departureTimeZone?.length) {
        dispatch({
          type: "legFieldChanged",
          payload: {
            legIndex: legIndex,
            field: "departureTimeZone",
            value: departureTimeZone,
          },
        });
      }
    }

    if (airportIndex === 1) {
      legData.arrivalAirport = {
        id: airport.value,
        name: airport.label,
      };

      let arrivalTimeZone = null;

      if (airport.tz && airport.tz.length) {
        arrivalTimeZone = airport.tz[0];
      }

      if (arrivalTimeZone?.length) {
        dispatch({
          type: "legFieldChanged",
          payload: {
            legIndex: legIndex,
            field: "arrivalTimeZone",
            value: arrivalTimeZone,
          },
        });
      }
    }

    await handleFlightTimeCalculationByIndex(legData, legIndex);
  };

  const handleAircraftChange = async (legIndex, updatedAircraft) => {
    let legData = { ...state.legs[legIndex] };

    legData.aircraft = updatedAircraft;

    await handleFlightTimeCalculationByIndex(legData, legIndex);
  };

  const handleFlightTimeCalculationByIndex = async (
    legData,
    legIndex,
    silent = false
  ) => {
    //todo - update syntax
    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    let params = {
      legData: {
        arrivalAirportId: legData.arrivalAirport.id,
        departureAirportId: legData.departureAirport.id,
        legIndex: legIndex,
        aircraftId: legData.aircraft?.id,
      },
    };

    let response = await ai
      .auth(token)
      .post(`/api/trips/flightTimes`, params)
      .catch((err) => {
        console.error(err.response.data);
        toast.error(
          "Please fill out all required fields: ensure an aircraft is selected for this Aircraft Option, and departure and arrival airports for all legs.",
          toastOptions
        );
        return;
      });

    if (
      response &&
      response?.data &&
      response?.data?.length &&
      response?.data[0]?.value
    ) {
      let returnedValueForLeg = response?.data[0]?.value;

      let composedString = "";

      if (returnedValueForLeg?.timeWithWindAdjustment) {
        composedString = timeFormatter(
          returnedValueForLeg?.timeWithWindAdjustment
        );
      }

      dispatch({
        type: "legFieldChanged",
        payload: {
          legIndex,
          field: "ete",
          value: composedString,
        },
      });

      if (!silent) {
        handleETEChange(legIndex, composedString);

        return toast.success(
          `ETE and ETA for Leg ${legIndex + 1} were updated.`
        );
      }
    }
  };

  const calculateETAForLeg = async (ete, etd, leg) => {
    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    let etaFactors = {
      ete,
      etd,
      leg,
    };

    //re-calculate ETA

    let response = await ai
      .auth(token)
      .post(`/api/trips/calculateETAOneLeg`, etaFactors)
      .catch((err) => {
        console.error(err.response.data);
        return;
      });

    return response;
  };

  const handleETDChange = async (legIndex, updatedETD) => {
    let leg = { ...state.legs[legIndex] };

    let pattern = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;

    const isValid = pattern.test(leg.ete);

    if (!isValid && leg.ete !== "" && leg.ete !== null) {
      dispatch({
        type: "legFieldChanged",
        payload: {
          legIndex: legIndex,
          field: "ete",
          value: "",
        },
      });

      return toast.error(
        `Please enter a valid ETE for Leg ${
          legIndex + 1
        } to recalculate the ETA.`,
        toastOptions
      );
    }

    let hours = parseInt(leg.ete.split(":")[0]);
    let minutes = parseInt(leg.ete.split(":")[1]);

    let etaResponse = await calculateETAForLeg(leg.ete, updatedETD, leg);
    if (!etaResponse.data) {
      return;
    }

    if (etaResponse.data?.eta) {
      dispatch({
        type: "legFieldChanged",
        payload: {
          field: "eta",
          value: etaResponse?.data?.eta,
          legIndex,
        },
      });
    }

    if (etaResponse.data?.departureTimeZone) {
      dispatch({
        type: "legFieldChanged",
        payload: {
          field: "departureTimeZone",
          value: etaResponse?.data?.departureTimeZone,
          legIndex,
        },
      });
    }

    if (etaResponse.data?.arrivalTimeZone) {
      dispatch({
        type: "legFieldChanged",
        payload: {
          field: "arrivalTimeZone",
          value: etaResponse?.data?.arrivalTimeZone,
          legIndex,
        },
      });
    }
  };

  const handleDateChange = async (legIndex, updatedDate) => {
    let leg = { ...state.legs[legIndex], departureDate: updatedDate };

    if (!leg.etd) {
      return;
    }

    let pattern = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;

    const isValid = pattern.test(leg.ete);

    if (!isValid || leg.ete === "" || leg.ete === null) {
      return;
    }

    let etaResponse = await calculateETAForLeg(leg.ete, leg.etd, leg);
    console.log(etaResponse)
    if (!etaResponse?.data) {
      return;
    }

    if (etaResponse.data?.eta) {
      dispatch({
        type: "legFieldChanged",
        payload: {
          field: "eta",
          value: etaResponse?.data?.eta,
          legIndex,
        },
      });
    }

    if (etaResponse.data?.departureTimeZone) {
      dispatch({
        type: "legFieldChanged",
        payload: {
          field: "departureTimeZone",
          value: etaResponse?.data?.departureTimeZone,
          legIndex,
        },
      });
    }

    if (etaResponse.data?.arrivalTimeZone) {
      dispatch({
        type: "legFieldChanged",
        payload: {
          field: "arrivalTimeZone",
          value: etaResponse?.data?.arrivalTimeZone,
          legIndex,
        },
      });
    }
  };

  const handleETEChange = async (legIndex, updatedETE) => {
    let leg = { ...state.legs[legIndex] };

    let pattern = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;

    const isValid = pattern.test(updatedETE);

    if (!isValid) {
      dispatch({
        type: "legFieldChanged",
        payload: {
          legIndex: legIndex,
          field: "ete",
          value: "",
        },
      });

      return toast.error(
        `Please enter a valid ETE for Leg ${legIndex + 1}`,
        toastOptions
      );
    }

    let hours = parseInt(updatedETE.split(":")[0]);
    let minutes = parseInt(updatedETE.split(":")[1]);

    if (!state.legs[legIndex]?.etd) {
      return toast.error(
        `Please enter a valid ETD for Leg ${
          legIndex + 1
        } to recalculate the ETA.`,
        toastOptions
      );
    }

    let etaResponse = await calculateETAForLeg(
      updatedETE,
      state.legs[legIndex]?.etd,
      leg
    );

    dispatch({
      type: "legFieldChanged",
      payload: {
        field: "eta",
        value: etaResponse.data?.eta,
        legIndex,
      },
    });

    if (etaResponse.data?.departureTimeZone) {
      dispatch({
        type: "legFieldChanged",
        payload: {
          field: "departureTimeZone",
          value: etaResponse.data?.departureTimeZone,
          legIndex,
        },
      });
    }

    if (etaResponse.data?.arrivalTimeZone) {
      dispatch({
        type: "legFieldChanged",
        payload: {
          field: "arrivalTimeZone",
          value: etaResponse.data?.arrivalTimeZone,
          legIndex,
        },
      });
    }
  };

  const handleFlightTimeCalculation = async (legData) => {
    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    if (!state.routingProcessed) {
      return;
    }

    let params = {
      legData: state.legs.flatMap((item, index) => {
        if (!item?.aircraft?.id) {
          return [];
        }

        return {
          arrivalAirportId: item.arrivalAirport.id,
          departureAirportId: item.departureAirport.id,
          legIndex: index,
          aircraftId: item?.aircraft?.id,
        };
      }),
    };

    if (params?.legData?.length === 0) {
      return;
    }

    let response = await ai
      .auth(token)
      .post(`/api/trips/flightTimesMulti`, params)
      .catch((err) => {
        console.error(err.response.data);
        toast.error(
          "Please fill out all required fields: ensure an aircraft is selected for this Aircraft Option, and departure and arrival airports for all legs.",
          toastOptions
        );
        return;
      });

    if (response && response.data) {
      dispatch({
        type: "flightTimesDownloaded",
        values: response.data,
      });
      return response.data;
    }
  };

  const handleFieldChange = (e) => {
    setUnsavedChanges({
      field: e.target.name,
      value: e.target.value !== state.initialFormValues[e.target.name],
    });

    dispatch({
      type: "fieldChanged",
      field: e.target.name,
      value: e.target.value,
      fieldType: e.target.type,
    });
  };

  const handlePriceFieldChange = (values, sourceInfo) => {
    const { formattedValue, value } = values;
    // Event is a Synthetic Event wrapper which holds target and other information. Source tells whether the reason for this function being triggered was an 'event' or due to a 'prop' change
    const { event, source } = sourceInfo;

    if (!event?.target) {
      return;
    }

    const initialVal = state.initialFormValues[event.target.name];
    const initialValParsed = initialVal ? parseFloat(initialVal) : initialVal

    const updatedValue = value ? parseFloat(value) : value;

    setUnsavedChanges({
      field: event.target.name,
      value: updatedValue !== initialValParsed,
    });

    dispatch({
      type: "fieldChanged",
      field: event.target.name,
      value: updatedValue,
    });
  };

  useEffect(() => {
    if (document) {
      document.getElementById("page-heading").scrollIntoView({
        behavior: "smooth",
      });
    }

    if (sourcingRecords?.length) {
      processSourcing(sourcingRecords);
    }

    processSubject();
  }, [state.loading, sourcingRecords]);

  const pageType = {
    [PAGE_TYPE.duplicate]: contractId && contractId !== "new" && action === "duplicate",
    [PAGE_TYPE.edit]: contractId && contractId !== "new" && state.contractIsNotSigned,
    [PAGE_TYPE.signedCannotEdit]: contractId && contractId !== "new" && !state.contractIsNotSigned,
  };
  pageType[PAGE_TYPE.newFromLead] = !pageType.duplicate && !pageType.edit && !pageType.signedCannotEdit && !tripId;
  pageType[PAGE_TYPE.newFromTrip] = !pageType.duplicate && !pageType.edit && !pageType.signedCannotEdit && tripId;

  const pageTypeKey = Object.keys(pageType).find(key => pageType[key]);

  const pageTitle =
    pageType.duplicate
      ? `Duplicate Contract`
      : pageType.edit
        ? `Edit Contract`
        : pageType.signedCannotEdit
          ? `Signed Contract - Cannot Edit`
          : `New Contract`;

  const pageBreadcrumbs = {
    [PAGE_TYPE.duplicate]: {
      breadcrumbs: [
        {link: '/prospects', label: 'Prospects'},
        {link: `/prospects/${state.prospect?._id}`, label: `${state.prospect?.firstName} ${state.prospect?.lastName}`},
        {label: pageTitle, isCurrentPage: true},
      ],
      goBackLink: `/prospects/${state.prospect?._id}`
    },
    [PAGE_TYPE.edit]: {
      breadcrumbs: [
        {link: '/trips', label: 'Trips'},
        {link: `/trips/${tripId}`, label: `Trip #${state.tripTag}`},
        {label: `${pageTitle} #${state.contractTag}`, isCurrentPage: true},
      ],
      goBackLink: `/trips/${tripId}`
    },
    [PAGE_TYPE.signedCannotEdit]: {
      breadcrumbs: [
        {link: '/trips', label: 'Trips'},
        {link: `/trips/${tripId}`, label: `Trip #${state.tripTag}`},
        {label: `${pageTitle} #${state.contractTag}`, isCurrentPage: true},
      ],
      goBackLink: `/trips/${tripId}`
    },
    [PAGE_TYPE.newFromLead]: {
      breadcrumbs: [
        {link: '/leads', label: 'Leads'},
        {link: `/leads/${state.leadData?._id}`, label: `Lead - ${state.leadData?.firstName} ${state.leadData?.lastName}`},
        {label: pageTitle, isCurrentPage: true},
      ],
      goBackLink: `/leads/${state.leadData?._id}`
    },
    [PAGE_TYPE.newFromTrip]: {
      breadcrumbs: [
        {link: '/trips', label: 'Trips'},
        {link: `/trips/${tripId}`, label: `Trip #${state.tripTag}`},
        {label: pageTitle, isCurrentPage: true},
      ],
      goBackLink: `/trips/${tripId}`
    },
  }

  useEffect(() => {
    setUnsavedChanges({
      field: "createProspectModal",
      value: state.createProspectModalIsOpen
    })
  }, [setUnsavedChanges, state.createProspectModalIsOpen]);

  useEffect(() => {
    setUnsavedChanges({
      field: "createAuthorizedSignerModal",
      value: state.createAuthorizedSignerModalIsOpen
    })
  }, [setUnsavedChanges, state.createAuthorizedSignerModalIsOpen]);

  console.log('UNSAVED CHANGES', state.unsavedChanges);

  useEffect(() => {
    if (state.legsDataChanged) {
      const arr1 = state.legs?.map(
        ({
           departureTimeZone,
           arrivalTimeZone,
           ete,
           etd,
           eta,
           ...item
      }) => item);

      const arr2 = state.initialFormValues.legs?.map(
        ({
           departureTimeZone,
           arrivalTimeZone,
           ete,
           etd,
           eta,
           ...item
        }) => item);

      setUnsavedChanges(({
        field: "legs",
        value: !isEqual(arr1, arr2),
      }));

      dispatch({
        type: "fieldChanged",
        field: "legsDataChanged",
        value: false,
      });
    }
  }, [
    setUnsavedChanges,
    state.legsDataChanged,
    state.legs,
    state.initialFormValues.legs
  ]);

  useEffect(() => {
    if (state.dusCancellationPolicyChanged) {
      setUnsavedChanges(({
        field: "dusCancellationPolicy",
        value: !isEqual(state.dusCancellationPolicy, state.initialFormValues.dusCancellationPolicy),
      }));

      dispatch({
        type: "fieldChanged",
        field: "dusCancellationPolicyChanged",
        value: false,
      });
    }
  }, [
    setUnsavedChanges,
    state.dusCancellationPolicyChanged,
    state.dusCancellationPolicy,
    state.initialFormValues.dusCancellationPolicy
  ]);

  useEffect(() => {
    if (state.contractCancellationPoliciesChanged) {
      const arr1 = (state.contractCancellationPolicies ?? []).map(({_id, ...item}) => item);

      const arr2 = (state.initialFormValues.contractCancellationPolicies ?? []).map(({_id, ...item}) => item);

      setUnsavedChanges(({
        field: "contractCancellationPolicies",
        value: !isEqual(arr1, arr2),
      }));

      dispatch({
        type: "fieldChanged",
        field: "contractCancellationPoliciesChanged",
        value: false,
      });
    }
  }, [
    setUnsavedChanges,
    state.contractCancellationPoliciesChanged,
    state.contractCancellationPolicies,
    state.initialFormValues.contractCancellationPolicies
  ]);

  return (
    <Fragment>
      {/* for scroll into view */}
      <div id="page-heading"/>
      {!state.loading && (
        <Fragment>
          <PageHeader
            pageTitle={pageTitle}
            breadcrumbs={pageBreadcrumbs[pageTypeKey].breadcrumbs}
            goBackLink={pageBreadcrumbs[pageTypeKey].goBackLink}
          />

          <form
            id="new-contract"
            onSubmit={handleContractSubmit}
            className="base-form"
          >
            <ProspectsAutocomplete
              onCreateOption={handleClientOptionCreation}
              handleProspectChange={handleClientChange}
              prospectOptions={[]}
              parentDispatch={dispatch}
              selectedProspect={state.selectedClient}
              label="Client"
              placeholder="Select a Client"
              required
            />

            <CreateProspectModal
              modalIsOpen={state.createProspectModalIsOpen}
              parentDispatch={dispatch}
              toast={toast}
              defaultPhoneNumber={state.modalPhoneNumber}
              toastOptions={toastOptions}
            />

            <div className="flex-row column-gap-1pct four-columns">
              <TextInput
                name="signerName"
                label="Signer Name"
                handleChange={handleTextInputChange}
                value={state.signerName}
              ></TextInput>

              <TextInput
                name="signerEmail"
                label="Signer Email"
                handleChange={handleTextInputChange}
                value={state.signerEmail}
                type={`email`}
              ></TextInput>

              <TextInput
                name="companyName"
                label="On Behalf Of"
                handleChange={handleTextInputChange}
                value={state.companyName}
                className={"companyName"}
              ></TextInput>

              <div className="form-group ml-0" key={`signerPhone`}>
                <label className="phoneInputLabel" htmlFor="phone">
                  Phone Number
                </label>

                <PhoneInput
                  placeholder="Phone Number"
                  value={state?.signerPhone}
                  name={`signerPhone`}
                  onChange={(value) => {
                    setUnsavedChanges({
                      field: "signerPhone",
                      value: value !== state.initialFormValues.signerPhone,
                    });

                    dispatch({
                      type: "fieldChanged",
                      field: "signerPhone",
                      value: value,
                    });
                  }}
                  onCountryChange={(value) => {
                    dispatch({
                      type: "fieldChanged",
                      field: "signerPhoneCountry",
                      value: value || "",
                    });
                  }}
                  defaultCountry={state?.signerPhoneCountry}
                  className="mb-0 base-phone-input"
                />
              </div>
            </div>

            {state.useDifferentAuthorizedSigner && false && (
              <>
                <div className="form-group mt20">
                  <label htmlFor="selectedAuthorizedSigner">
                    Authorized Signer
                  </label>

                  <CreatableSelect
                    className="authorized-signer-select"
                    classNamePrefix="select"
                    placeholder="Authorized Signer"
                    isClearable
                    options={state.authorizedSigners}
                    onChange={(val) => {
                      setUnsavedChanges({
                        field: "selectedAuthorizedSigner",
                        value: val?._id !== state.initialFormValues.selectedAuthorizedSigner?._id,
                      });

                      dispatch({
                        type: "fieldChanged",
                        field: "selectedAuthorizedSigner",
                        value: val,
                      })
                    }}
                    value={state.selectedAuthorizedSigner}
                    name={"selectedAuthorizedSigner"}
                    onCreateOption={handleAuthorizedSignerOptionCreation}
                    formatCreateLabel={(inputValue) =>
                      "Add an Authorized Signer"
                    }
                  ></CreatableSelect>
                </div>

                <CreateAuthorizedSignerModal
                  modalIsOpen={state.createAuthorizedSignerModalIsOpen}
                  parentDispatch={dispatch}
                  toast={toast}
                  toastOptions={toastOptions}
                  modalStateKey={`createAuthorizedSignerModalIsOpen`}
                />
              </>
            )}

            <div className="flight-details-container">
              <h5>Routing</h5>

              {state?.legs?.length > 0 &&
                state?.legs?.map((leg, index) => (
                  <div className="flight-details-block" key={index}>
                    <div className="flex-row column-gap-1pct">
                      <div className="datepickerContainer mb-0">
                        <label
                          htmlFor={`departureDate[${index}]`}
                          className="base-label"
                        >
                          Date <span className="required">*</span>
                        </label>

                        <Datepicker
                          value={leg.departureDate}
                          onChange={(event) => {
                            dispatch({
                              type: "legFieldChanged",
                              payload: {
                                legIndex: index,
                                field: "departureDate",
                                value: event.value,
                              },
                            });

                            handleDateChange(index, event.value);
                          }}
                          dataTimezone="utc"
                          displayTimezone="utc"
                          timezonePlugin={momentTimezone}
                          controls={["date"]}
                        />
                      </div>
                      <div className={`form-group flex-half`}>
                        <label htmlFor={`etd[${index}]`}>
                          ETD <span className="required">*</span>
                        </label>

                        <Datepicker
                          onChange={(e) => {
                            handleETDChange(index, e.value);

                            dispatch({
                              type: "legFieldChanged",
                              payload: {
                                legIndex: index,
                                field: "etd",
                                value: e.value || null,
                              },
                            });
                          }}
                          timezonePlugin={momentTimezone}
                          dataTimezone="utc"
                          displayTimezone={leg.departureTimeZone}
                          controls={["time"]}
                          value={leg.etd}
                          defaultSelection="12:00 am"
                        ></Datepicker>
                      </div>

                      <div className={`form-group flex-half`}>
                        <label htmlFor={`eta[${index}]`}>ETA</label>

                        <Datepicker
                          onChange={(e) => {
                            dispatch({
                              type: "legFieldChanged",
                              payload: {
                                legIndex: index,
                                field: "eta",
                                value: e.value || null,
                              },
                            });
                          }}
                          timezonePlugin={momentTimezone}
                          dataTimezone="utc"
                          displayTimezone={leg.arrivalTimeZone}
                          controls={["time"]}
                          value={leg.eta}
                          defaultSelection="12:00 am"
                        ></Datepicker>
                      </div>

                      <div className="airportContainer flex-grow-1 mb-0">
                        <label
                          htmlFor={`departureAirport[${index}]`}
                          className="base-label"
                        >
                          Depart <span className="required">*</span> (
                          {leg.departureTimeZone})
                        </label>

                        <AsyncSelect
                          key={`departure-${leg.departureAirport?.airportName}`}
                          name={`departureAirport[${index}]`}
                          className="departure-airport-select mb-0"
                          classNamePrefix="select"
                          cacheOptions
                          loadOptions={loadAirportOptions}
                          placeholder="Search Airports"
                          defaultValue={{
                            label: leg.departureAirport.airportName,
                            value: leg.departureAirport.id,
                          }}
                          onChange={(selectedAirport) => {
                            handleAirportChange(index, selectedAirport, 0);

                            dispatch({
                              type: "legFieldChanged",
                              payload: {
                                legIndex: index,
                                field: "departureAirport",
                                value: {
                                  airportName: selectedAirport?.label || "",
                                  id: selectedAirport?.value || "",
                                  code: selectedAirport?.code || "",
                                  city: selectedAirport?.city || "",
                                  localCode: selectedAirport?.localCode || "",
                                },
                              },
                            });
                          }}
                        />
                      </div>
                      <div className="airportContainer flex-grow-1">
                        <label
                          htmlFor={`departureAirport[${index}]`}
                          className="base-label"
                        >
                          Arrive <span className="required">*</span> (
                          {leg.arrivalTimeZone})
                        </label>

                        <AsyncSelect
                          key={`arrival-${leg.arrivalAirport.airportName}`}
                          name={`arrivalAirport[${index}]`}
                          className="arrival-airport-select mb-0"
                          classNamePrefix="select"
                          cacheOptions
                          loadOptions={loadAirportOptions}
                          placeholder="Search Airports"
                          defaultValue={{
                            label: leg.arrivalAirport.airportName,
                            value: leg.arrivalAirport.id,
                          }}
                          onChange={(selectedAirport) => {
                            handleAirportChange(index, selectedAirport, 1);

                            dispatch({
                              type: "legFieldChanged",
                              payload: {
                                legIndex: index,
                                field: "arrivalAirport",
                                value: {
                                  airportName: selectedAirport?.label || "",
                                  id: selectedAirport?.value || "",
                                  code: selectedAirport?.code || "",
                                  city: selectedAirport?.city || "",
                                  localCode: selectedAirport?.localCode || "",
                                },
                              },
                            });
                          }}
                        />
                      </div>
                    </div>

                    <div className="flex-row column-gap-1pct">
                      <TextInput
                        name={`ete[${index}]`}
                        label="ETE"
                        handleChange={(e) => {
                          dispatch({
                            type: "legFieldChanged",
                            payload: {
                              legIndex: index,
                              field: "ete",
                              value: e.target.value || "",
                            },
                          });
                        }}
                        handleBlur={(e) => {
                          handleETEChange(index, e.target.value);
                        }}
                        className="base-input"
                        containerClasses="flex-half mb-0"
                        value={leg.ete}
                      />

                      <TextInput
                        type={"number"}
                        name={`passengers[${index}]`}
                        label="PAX"
                        handleChange={(e) => {
                          dispatch({
                            type: "legFieldChanged",
                            payload: {
                              legIndex: index,
                              field: "passengers",
                              value: parseInt(e.target.value) || "",
                            },
                          });
                        }}
                        className="base-input"
                        containerClasses="flex-half mb-0"
                        value={leg.passengers}
                        required
                      ></TextInput>

                      <div className="form-group flex-grow-1">
                        <label htmlFor={`aircraft[${index}]`}>Aircraft</label>
                        <AsyncSelect
                          name={`aircraft[${index}]`}
                          className="aircraft-select mb-0"
                          classNamePrefix="select"
                          cacheOptions
                          key={`aircraft[${index}]${leg?.aircraft?.name}`}
                          loadOptions={loadAircraftOptions}
                          placeholder="Search Aircraft"
                          defaultValue={{
                            label: leg?.aircraft?.name,
                            value: leg?.aircraft?.id,
                          }}
                          onChange={(selectedAircraft) => {
                            if (selectedAircraft) {
                              handleAircraftChange(index, selectedAircraft);

                              dispatch({
                                type: "legFieldChanged",
                                payload: {
                                  legIndex: index,
                                  field: "aircraft",
                                  value: {
                                    name: selectedAircraft.name,
                                    seats: selectedAircraft.seats,
                                    yom: selectedAircraft.yom,
                                    id: selectedAircraft.id,
                                  },
                                },
                              });
                            }

                            if (!selectedAircraft) {
                              dispatch({
                                type: "legFieldChanged",
                                payload: {
                                  legIndex: index,
                                  field: "aircraft",
                                  value: {},
                                },
                              });
                            }
                          }}
                          isClearable
                        />
                      </div>
                    </div>

                    {index >= 1 && (
                      <button
                        className="deleteLeg base-button base-gunmetal-button"
                        onClick={(e) => {
                          e.preventDefault();
                          return dispatch({ type: "legDeleted", index: index });
                        }}
                        type="button"
                      >
                        Delete Leg {index + 1}
                      </button>
                    )}
                  </div>
                ))}
              <button
                className="base-button evo-blue mt-0"
                onClick={handleAddLeg}
                type="button"
              >
                Add Leg
              </button>
            </div>

            <ContractCancellationPolicies
              parentDispatch={dispatch}
              contractCancellationPolicies={state.contractCancellationPolicies}
              dusCancellationPolicy={state.dusCancellationPolicy}
            ></ContractCancellationPolicies>

            <div className="supplemental-terms-container mt-20">
              <h5>Supplemental Terms</h5>
              <p className="form-help-text">
                Optional supplemental terms to be included in the contract
              </p>

              <Editor
                editorState={state.supplementalTermsEditor}
                wrapperClassName="header-editor-wrapper"
                editorClassName="notes-editor"
                onEditorStateChange={(editorState) => {
                  setUnsavedChanges({
                    field: 'supplementalTermsEditor',
                    value:
                      editorState?.getCurrentContent().getPlainText()
                      !== state.initialFormValues.supplementalTermsEditor?.getCurrentContent().getPlainText(),
                  });

                  dispatch({
                    type: "fieldChanged",
                    field: "supplementalTermsEditor",
                    value: editorState,
                  })
                }}
              />
            </div>

            {/* Hide Contract Message for new contracts - show only for historical ones */}
            {contractId &&
              contractId !== "new" &&
              state.contractNotesEditorState && (
                <div className="notes-container">
                  <h3>Contract Message</h3>

                  <Editor
                    editorState={state.contractNotesEditor}
                    wrapperClassName="header-editor-wrapper"
                    editorClassName="notes-editor"
                    onEditorStateChange={(editorState) => {
                      setUnsavedChanges({
                        field: 'contractNotesEditor',
                        value:
                          editorState?.getCurrentContent().getPlainText()
                          !== state.initialFormValues.contractNotesEditor?.getCurrentContent().getPlainText(),
                      });

                      dispatch({
                        type: "fieldChanged",
                        field: "contractNotesEditor",
                        value: editorState,
                      })
                    }}
                  />
                </div>
              )}

            <div className="pricing-container">
              <h5>Pricing</h5>

              <p>
                <strong>
                  {`Flight Charges${
                    state.paymentType === "cc" ? " (Subtotal)" : ``
                  }: `}
                  {new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                    minimumFractionDigits: 2,
                  }).format(state.flightCharges)}
                </strong>
              </p>

              {state.paymentType === "cc" &&
                !isNaN(parseFloat(state.ccFee)) && (
                  <p>
                    <strong>
                      Flight Charges (Grand Total){" "}
                      {new Intl.NumberFormat("en-US", {
                        style: "currency",
                        currency: "USD",
                        minimumFractionDigits: 2,
                      }).format(
                        parseFloat(state.flightCharges) +
                          parseFloat(state.ccFee)
                      )}
                    </strong>
                  </p>
                )}

              <div className="form-group">
                <label>Total Flight Charges (Incl. Fees, Except CC Fee)</label>

                <NumberFormat
                  value={state.flightCharges}
                  thousandSeparator={true}
                  prefix={"$"}
                  onValueChange={handlePriceFieldChange}
                  placeholder="Flight Charges"
                  className="semibold-text"
                  decimalScale={2}
                  name="flightCharges"
                />
              </div>

              <FormControl className="form-group base-radio-group">
                <label id="payment-type-label">
                  Payment Type <span className="required">*</span>{" "}
                </label>

                <RadioGroup
                  aria-labelledby="payment-type-label"
                  name="paymentType"
                  value={state.paymentType}
                  onChange={handleFieldChange}
                >
                  <FormControlLabel
                    value="wire"
                    control={<Radio />}
                    label="Wire"
                  />
                  <FormControlLabel
                    value="cc"
                    control={<Radio />}
                    label="Credit Card"
                  />
                </RadioGroup>
              </FormControl>

              {state.paymentType === "cc" && (
                <div className="form-group">
                  <label>CC Processing Fee</label>

                  <NumberFormat
                    value={state.ccFee}
                    thousandSeparator={true}
                    prefix={"$"}
                    onValueChange={handlePriceFieldChange}
                    placeholder="CC Processing Fee"
                    className="semibold-text"
                    decimalScale={2}
                    name="ccFee"
                  />
                </div>
              )}

              <div className="form-group">
                <label>FET</label>

                <NumberFormat
                  value={state.FET}
                  thousandSeparator={true}
                  prefix={"$"}
                  onValueChange={handlePriceFieldChange}
                  placeholder="FET"
                  className="semibold-text"
                  decimalScale={2}
                  name="FET"
                />
              </div>

              <div className="form-group">
                <label>Segment Fees</label>

                <NumberFormat
                  value={state.segmentFees}
                  thousandSeparator={true}
                  prefix={"$"}
                  onValueChange={handlePriceFieldChange}
                  placeholder="Segment Fees"
                  className="semibold-text"
                  decimalScale={2}
                  name="segmentFees"
                />
              </div>

            </div>

            <LoadingButton
              className="base-button evo-blue w-100 mt-10"
              type="submit"
              disabled={action !== "duplicate" && !state.contractIsNotSigned}
              isLoading={state.isFormLoading}
            >
              Save and Preview
            </LoadingButton>
          </form>

          <StateDisplay user={user} data={state}></StateDisplay>
        </Fragment>
      )}

      <UnsavedChangesWarningModal isChanged={!!Object.keys(state.unsavedChanges).length}/>
    </Fragment>
  );
};

export default NewContractContent;
